import { type StyleProps, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { createEditor } from 'slate'
import {
  Editable,
  type RenderElementProps,
  type RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react'

import logger from '@/utils/logger'

import deserializeRichText from '@/client/services/richText/deserializeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'

import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import LatexContainer from '../LatexContainer/LatexContainer'
import RichTextElement from './internal/RichTextElement'
import RichTextLeaf from './internal/RichTextLeaf'

interface Props {
  content: SerializedRichText | null
  styleProps?: StyleProps
}

function RichTextSlate({
  content,
  styleProps,
}: {
  content: SerializedRichText | null
  styleProps?: StyleProps
}): React.ReactElement {
  const editor = useMemo(() => withReact(createEditor()), [])
  const renderElement = React.useCallback(
    (props: RenderElementProps) => <RichTextElement {...props} styleProps={styleProps} />,
    [styleProps]
  )
  const renderLeaf = React.useCallback(
    (props: RenderLeafProps) => <RichTextLeaf {...props} styleProps={styleProps} />,
    [styleProps]
  )
  const value = useMemo(() => deserializeRichText(content), [content])
  editor.children = value

  return (
    <Slate editor={editor} value={value}>
      <LatexContainer>
        <Editable renderElement={renderElement} renderLeaf={renderLeaf} readOnly />
      </LatexContainer>
    </Slate>
  )
}

function RichTextInternal({ content, styleProps }: Props): React.ReactElement {
  if (
    content == null ||
    content.serialization_method === 'slate_json_v1' ||
    content.serialization_method === 'plain_text'
  ) {
    return <RichTextSlate content={content} styleProps={styleProps} />
  }

  // Anees - July 15, 2025 - We have done a bad job of securing our rich text ingress
  // so I'm cutting off the rendering. I dont want a security hole in our app.
  if (content.serialization_method === 'raw_html') {
    return <Text color="fg.muted">Oops! There was an error displaying this content.</Text>
  }

  logger.error('RichText: unknown serialization method', { content })

  return <Text color="fg.muted">Oops! There was an error displaying this content.</Text>
}

const RichText = withErrorBoundary(RichTextInternal, () => (
  <Text color="fg.muted">Oops! There was an error displaying this content.</Text>
))

export default RichText
